/* global Excel, Office */

// Matches our functions anywhere in a formula, including nested uses like
// =IFERROR(SHEETYBANKS.METRICS(...), ""). Excel canonicalizes function names
// to uppercase in formula text.
export const FUNCTION_MARKER = "SHEETYBANKS.";

// getSpecialCells needs ExcelApi 1.9 and getSpillParent/getSpillingToRange
// need 1.12; hosts without them (perpetual Office 2016/2019) can't scope a
// refresh or snapshot cells safely.
export const REQUIRED_API_SET = "1.12";

export function canUseScopedApis(): boolean {
  return Office.context.requirements.isSetSupported("ExcelApi", REQUIRED_API_SET);
}

export interface FormulaCandidate {
  ws: Excel.Worksheet;
  cell: Excel.Range;
  formula: string;
}

/**
 * Filters scan candidates down to cells that own their formula. Every cell
 * in a spilled range reports a spill parent — including the anchor, which
 * reports itself — so a candidate is a writable anchor when it has no spill
 * parent or the parent is the cell itself. (Checking isNullObject alone
 * would drop spilled anchors and exclude every spilling formula.)
 * Leaves each candidate's cell with its address loaded.
 */
export async function filterToSpillAnchors(
  context: Excel.RequestContext,
  candidates: FormulaCandidate[]
): Promise<FormulaCandidate[]> {
  const spillParents = candidates.map(({ cell }) => {
    const spillParent = cell.getSpillParentOrNullObject();
    spillParent.load("address");
    cell.load("address");
    return spillParent;
  });
  await context.sync();

  return candidates.filter(
    ({ cell }, i) => spillParents[i].isNullObject || spillParents[i].address === cell.address
  );
}

/**
 * Finds every cell in the workbook whose formula calls a SHEETYBANKS
 * function. Spill members are included (getSpecialCells reports them as
 * formula cells); callers that write must skip cells with a spill parent.
 * Protected sheets are skipped entirely — writing to one fails the whole
 * batch, so their cells are better left untouched than aborting everything.
 */
export async function scanForSheetyBanksFormulas(
  context: Excel.RequestContext
): Promise<FormulaCandidate[]> {
  const sheets = context.workbook.worksheets;
  sheets.load("items/name");
  await context.sync();

  const perSheet = sheets.items.map((ws) => {
    ws.protection.load("protected");
    const formulaCells = ws
      .getUsedRangeOrNullObject()
      .getSpecialCellsOrNullObject(Excel.SpecialCellType.formulas);
    formulaCells.load("areas/items/formulas");
    return { ws, formulaCells };
  });
  await context.sync();

  const candidates: FormulaCandidate[] = [];
  for (const { ws, formulaCells } of perSheet) {
    if (ws.protection.protected || formulaCells.isNullObject) continue;
    for (const area of formulaCells.areas.items) {
      const grid = area.formulas;
      for (let r = 0; r < grid.length; r++) {
        for (let c = 0; c < grid[r].length; c++) {
          const formula = grid[r][c];
          if (
            typeof formula === "string" &&
            formula.toUpperCase().indexOf(FUNCTION_MARKER) !== -1
          ) {
            candidates.push({ ws, cell: area.getCell(r, c), formula });
          }
        }
      }
    }
  }
  return candidates;
}
